import React, { useContext, useEffect, useMemo } from 'react';
import { SalesforceContext, Steps, WalletTypes } from 'src/context/salesforce-context';
import FileUpload from 'src/components/file-upload/file-upload';
import TealiumEvents from 'src/helpers/tealium-events';
import MiniAppEvents from 'src/helpers/miniapp-events';
import { BUSINESS_TYPE_KEY_TO_NAME_LOOKUP, OnboardingDocumentType } from 'src/helpers/constants';
import { CONSUMER_TYPE } from 'src/services/salesforce.service';
import { ForeignNationalType, IdentityTypeDirectors } from 'src/features/common';
import { Environment } from 'src/Environment';
import { stripWhitespace } from 'src/helpers/formatting';
import './upload-documents.scss';

export default function UploadDocuments(props) {
  const inMiniProgram = MiniAppEvents.isInMiniApp();
  var companyResolutionLetter = false;
  var npoFoundingDoucmentation = false;
  var trustDeed = false;
  var trustResolutionLetter = false;
  const {
    documents,
    setDocuments,
    merchant,
    lead,
    customerType,
    deletedDocs,
    setDeletedDocs,
    getSettlementsMethod,
  } = useContext(SalesforceContext);
  const NON_ID_SIZE_LIMIT = 3_072_000;
  const ID_SIZE_LIMIT = 1_024_000;

  const showSov = getSettlementsMethod() === WalletTypes.SOV;
  const isPendingApp = useMemo(() => !!merchant.isPendingApp, [merchant.isPendingApp]);
  const foreignNationalsEnabled = !Environment.current?.disabledFeatures?.passport;

  const getHintsFor = (type) => {
    const uploadDocUpdates = (merchant?.pageUpdates || []).find(
      (update) => update.subStepName.toLowerCase() === Steps.UPLOAD_DOCUMENTS.toLowerCase(),
    );
    return uploadDocUpdates?.fields.find(({ fieldName }) => fieldName === type)?.hints;
  };

  useEffect(() => {
    if (merchant?.account?.business_type) {
      const assistantId = lead.dealerId || lead.agentId || '';
      TealiumEvents.uploadDocsPageLoadBankingDetailsFormEndCombined(
        merchant.account.business_type,
        customerType,
        assistantId,
        merchant.isPendingApp,
      );
    }
  }, [merchant?.account?.business_type]);

  useEffect(() => {
    if (validateUploadedDocs()) {
      props.toggleNext(true);
    } else {
      props.toggleNext(false);
    }
  }, [documents]);

  const textTable = {
    dealer: {
      title: 'Upload documents',
      subTitle:
        'For us to verify the customer and their business, please upload the following documents',
      idText:
        'For Smart ID please upload the front side of the ID, with all the personal details listed. For National Green book ID, upload the page with all the personal details (in JPEG format)',
    },
    agent: {
      title: 'Upload documents',
      subTitle:
        'For us to verify the customer and their business, please upload the following documents',
      idText:
        'For Smart ID please upload the front side of the ID, with all the personal details listed. For National Green book ID, upload the page with all the personal details (in JPEG format)',
    },
    normal: {
      title:
        lead.consumerType === CONSUMER_TYPE.owner ? 'Upload your documents' : 'Upload documents',
      subTitle:
        lead.consumerType === CONSUMER_TYPE.owner
          ? 'For us to verify you and your business, please upload the following documents'
          : 'For us to verify the customer and their business, please upload the following documents',
      idText:
        lead.consumerType === CONSUMER_TYPE.owner
          ? 'For Smart ID please upload the front side of your ID, with all your personal details listed. For National Green book ID, upload the page with all your personal details (in JPEG format)'
          : 'For Smart ID please upload the front side of the ID, with all the personal details listed. For National Green book ID, upload the page with all the personal details (in JPEG format)',
    },
    pos: {
      title: 'Upload your documents',
      subTitle: 'For us to verify you and your business, please upload the following documents',
      idText:
        'For Smart ID please upload the front side of your ID, with all your personal details listed. For National Green book ID, upload the page with all your personal details (in JPEG format)',
    },
  };

  const getIdsAndPassports = () => {
    const ids = (merchant.account?.directors || [])
      .filter(
        (director) =>
          director.id_type === IdentityTypeDirectors.RSA_ID ||
          director.id_type === IdentityTypeDirectors.PASSPORT,
      )
      .map((director) => ({
        id_type: director.id_type,
        id_number: director.directors_id_number,
      }));

    if (
      merchant.contact.id_type === IdentityTypeDirectors.RSA_ID ||
      merchant.contact.id_type === IdentityTypeDirectors.PASSPORT
    ) {
      ids.push({
        id_type: merchant.contact.id_type,
        id_number: merchant.contact.id_number,
      });
    }

    return Array.from(new Set(ids));
  };

  const getForeignNationalIds = (idType) => {
    const passports = (merchant.account?.directors || [])
      .filter((director) => director.id_type === idType)
      .map((director) => ({
        id_type: idType,
        id_number: director.directors_id_number,
      }));

    if (merchant.contact.id_type === idType) {
      passports.push({
        id_type: idType,
        id_number: merchant.contact.id_number,
      });
    }

    return Array.from(new Set(passports));
  };

  const getWorkPermitIds = () => {
    const idHolders = (merchant.account?.directors || [])
      .filter(
        ({ id_type }) =>
          /* id_type === IdentityTypeDirectors.REFUGEE || */
          id_type === IdentityTypeDirectors.PASSPORT,
      )
      .map((idHolder) => ({
        id_type: idHolder.id_type || '',
        id_number: stripWhitespace(idHolder.directors_id_number) || '',
      }));

    if (
      merchant.contact.id_type === ForeignNationalType.PASSPORT
      /* || merchant.contact.id_type === ForeignNationalType.REFUGEE */
    ) {
      idHolders.push({
        id_type: merchant.contact.id_type || '',
        id_number: stripWhitespace(merchant.contact.id_number) || '',
      });
    }

    return Array.from(new Set(idHolders));
  };

  const getPassports = () => {
    const passports = (merchant.account?.directors || [])
      .filter((director) => director.id_type === IdentityTypeDirectors.PASSPORT)
      .map((director) => ({
        id_type: IdentityTypeDirectors.PASSPORT,
        id_number: director.directors_id_number,
      }));

    if (merchant.contact.id_type === IdentityTypeDirectors.PASSPORT) {
      passports.push({
        id_type: IdentityTypeDirectors.PASSPORT,
        id_number: merchant.contact.id_number,
      });
    }

    return Array.from(new Set(passports));
  };

  const businessType = lead.typeOfBusiness;
  if (businessType === 'Company' || businessType === 'Close Corporation') {
    companyResolutionLetter = true;
  } else if (businessType === 'NPO') {
    companyResolutionLetter = true;
    npoFoundingDoucmentation = true;
  } else if (businessType === 'Trust') {
    trustDeed = true;
    trustResolutionLetter = true;
  } else if (businessType === 'Partnership') {
    companyResolutionLetter = true;
  }

  const validateUploadedDocs = () => {
    const docTypes = [];
    // eslint-disable-next-line no-unused-expressions
    documents?.documents?.forEach((doc) => docTypes.push(doc.document_type));

    const companyResUploaded = companyResolutionLetter
      ? docTypes.includes(OnboardingDocumentType.COMPANY_RES_TYPE)
      : true;
    const npoFoundingUploaded = npoFoundingDoucmentation
      ? docTypes.includes(OnboardingDocumentType.NPO_FOUNDING_TYPE)
      : true;
    const trustDeedUploaded = trustDeed
      ? docTypes.includes(OnboardingDocumentType.TRUST_DEED_TYPE)
      : true;
    const trustResolutionUploaded = trustResolutionLetter
      ? docTypes.includes(OnboardingDocumentType.TRUST_RES_TYPE)
      : true;
    const sovSoleProp =
      businessType === BUSINESS_TYPE_KEY_TO_NAME_LOOKUP.sole_prop && !showSov
        ? docTypes.includes(OnboardingDocumentType.BANK_PROOF_TYPE)
        : true;
    // const sovResidence = sovRequested
    //   ? docTypes.includes(OnboardingDocumentType.RESIDENCE_TYPE)
    //   : true;

    const allIdDocsUploaded = () => {
      const ids = getIdsAndPassports().map((id) => stripWhitespace(id.id_number));

      return (
        ids.filter(
          (id) =>
            documents.documents?.find(
              (doc) =>
                (doc.document_type === OnboardingDocumentType.OLD_GREEN_IDENTITY_DOCUMENT_TYPE ||
                  doc.document_type === OnboardingDocumentType.NEW_IDENTITY_CARD_TYPE ||
                  doc.document_type === OnboardingDocumentType.PASSPORT_TYPE) &&
                id === doc.id_number,
            ) !== undefined,
        ).length >= ids.length
      );
    };

    const allWorkPermsOrPermResUploaded = () => {
      if (!foreignNationalsEnabled || getWorkPermitIds().length === 0) {
        return true;
      }

      const ids = getWorkPermitIds();

      return (
        ids.filter(
          (idHolder) =>
            documents.documents?.find(
              (doc) =>
                (doc.document_type === OnboardingDocumentType.WORK_PERMIT_TYPE &&
                  idHolder.id_type === IdentityTypeDirectors.REFUGEE &&
                  idHolder.id_number === doc.id_number) ||
                ((doc.document_type === OnboardingDocumentType.WORK_PERMIT_TYPE ||
                  doc.document_type === OnboardingDocumentType.PERMANENT_RESIDENCY_TYPE) &&
                  idHolder.id_number === doc.id_number),
            ) !== undefined,
        ).length >= ids.length
      );
    };

    const allFilesUploadedForDirectorTypeAndDocType = (directorType, docType) => {
      if (!foreignNationalsEnabled || getForeignNationalIds(directorType).length === 0) {
        return true;
      }

      const ids = getForeignNationalIds(directorType).map((id) => stripWhitespace(id.id_number));

      return (
        ids.filter(
          (id) =>
            documents.documents?.find(
              (doc) => doc.document_type === docType && id === doc.id_number,
            ) !== undefined,
        ).length >= ids.length
      );
    };

    return (
      allIdDocsUploaded() &&
      companyResUploaded &&
      npoFoundingUploaded &&
      trustDeedUploaded &&
      trustResolutionUploaded &&
      sovSoleProp &&
      allWorkPermsOrPermResUploaded() &&
      allFilesUploadedForDirectorTypeAndDocType(
        IdentityTypeDirectors.REFUGEE,
        OnboardingDocumentType.REFUGEE_LETTER,
      ) &&
      allFilesUploadedForDirectorTypeAndDocType(
        IdentityTypeDirectors.ASYLUM_SEEKER,
        OnboardingDocumentType.ASYLUM_SEEKER,
      )
      // && sovResidence
    );
  };

  const updateDeletedDocs = (docId) => {
    setDeletedDocs([...deletedDocs, docId]);
  };

  const config = [
    {
      props: {
        title: 'SA ID / Passport',
        subtext: textTable[customerType]?.idText,
        boldedText:
          getIdsAndPassports().length > 1
            ? `Make sure you upload copies of ID documents for all the directors.`
            : '',
        fileLimitText: '',
        documentType: OnboardingDocumentType.ID_TYPE,
        idNumbers: getIdsAndPassports(),
        sizeLimit: ID_SIZE_LIMIT,
        allowedFileTypes: ['image/jpeg', 'image/png'],
        unsupportedFileTypeErrorMessage:
          'Upload failed, We only support JPEG/PNG file types. Please try again.',
        currentDocuments: documents,
        setCurrentDocuments: setDocuments,
        setDeletedDocs: updateDeletedDocs,
        maxFiles: getIdsAndPassports().length,
        automationIdPrefix: 'Onboarding_Upload_Documents_File_Upload_ID_Document',
        inMiniProgram: inMiniProgram,
        onShowIdModal: () =>
          TealiumEvents.uploadDocsSuccessfulPageView(
            merchant?.account?.business_type,
            customerType,
            merchant.isPendingApp,
          ),
        onDocumentUpload: () =>
          TealiumEvents.uploadDocsUpload(
            OnboardingDocumentType.ID_TYPE,
            customerType,
            merchant.isPendingApp,
          ),
        onUploadError: (errorMessage) => TealiumEvents.uploadError(errorMessage),
        customerType: customerType,
        isPending: isPendingApp,
        hints: getHintsFor(OnboardingDocumentType.ID_TYPE),
      },
      showOnlyIf: () => getIdsAndPassports().length > 0,
    },
    {
      props: {
        title: 'Refugee Letter',
        subtext:
          'The document issued by Home Affairs stating your official refugee status. This document must be valid for at least 6 months.',
        fileLimitText: 'Maximum size: 3MB',
        documentType: OnboardingDocumentType.REFUGEE_LETTER,
        idNumbers: getForeignNationalIds(IdentityTypeDirectors.REFUGEE),
        sizeLimit: NON_ID_SIZE_LIMIT,
        allowedFileTypes: ['image/jpeg'],
        unsupportedFileTypeErrorMessage:
          'Upload failed, We only support JPEG file type. Please try again.',
        currentDocuments: documents,
        setCurrentDocuments: setDocuments,
        setDeletedDocs: updateDeletedDocs,
        automationIdPrefix: 'Onboarding_Upload_Documents_File_Upload_Refugee_Document',
        inMiniProgram: inMiniProgram,
        customerType: customerType,
        isPending: isPendingApp,
        hints: getHintsFor(OnboardingDocumentType.REFUGEE_LETTER),
      },
      showOnlyIf: () =>
        foreignNationalsEnabled && getForeignNationalIds(IdentityTypeDirectors.REFUGEE).length > 0,
    },
    {
      props: {
        title: 'Asylum Seeker Document',
        subtext:
          'The document issued by Home Affairs stating your asylum status. This document must be valid for at least 6 months.',
        fileLimitText: 'Maximum size: 3MB',
        documentType: OnboardingDocumentType.ASYLUM_SEEKER,
        idNumbers: getForeignNationalIds(IdentityTypeDirectors.ASYLUM_SEEKER),
        sizeLimit: NON_ID_SIZE_LIMIT,
        allowedFileTypes: ['image/jpeg'],
        unsupportedFileTypeErrorMessage:
          'Upload failed, We only support JPEG file type. Please try again.',
        currentDocuments: documents,
        setCurrentDocuments: setDocuments,
        setDeletedDocs: updateDeletedDocs,
        automationIdPrefix: 'Onboarding_Upload_Documents_File_Upload_Asylum_Seeker_Document',
        inMiniProgram: inMiniProgram,
        customerType: customerType,
        isPending: isPendingApp,
        hints: getHintsFor(OnboardingDocumentType.ASYLUM_SEEKER),
      },
      showOnlyIf: () =>
        foreignNationalsEnabled &&
        getForeignNationalIds(IdentityTypeDirectors.ASYLUM_SEEKER).length > 0,
    },
    {
      props: {
        title: 'Work Permit',
        subtext:
          'The document issued by Home Affairs stating that you (Passport holders/ Refugees) are allowed to live and work in South Africa. This document must be valid for at least 6 months.',
        fileLimitText: 'Maximum size: 3MB',
        documentType: OnboardingDocumentType.WORK_PERMIT_TYPE,
        passportNumbers: getWorkPermitIds(),
        sizeLimit: NON_ID_SIZE_LIMIT,
        allowedFileTypes: ['image/jpeg', 'application/pdf'],
        unsupportedFileTypeErrorMessage:
          'Upload failed, We only support JPEG and PDF file types. Please try again.',
        currentDocuments: documents,
        setCurrentDocuments: setDocuments,
        setDeletedDocs: updateDeletedDocs,
        automationIdPrefix: 'Onboarding_Upload_Documents_File_Upload_Work_Permit',
        inMiniProgram: false,
        isPending: isPendingApp,
        hints: getHintsFor(OnboardingDocumentType.WORK_PERMIT_TYPE),
      },
      showOnlyIf: () => foreignNationalsEnabled && getPassports().length > 0,
    },
    {
      props: {
        title: 'Permanent Residency',
        subtext:
          'The document issued by Home Affairs stating that you are a permanent South African resident.',
        fileLimitText: 'Maximum size: 3MB',
        documentType: OnboardingDocumentType.PERMANENT_RESIDENCY_TYPE,
        passportNumbers: getPassports(),
        sizeLimit: NON_ID_SIZE_LIMIT,
        allowedFileTypes: ['image/jpeg', 'application/pdf'],
        unsupportedFileTypeErrorMessage:
          'Upload failed, We only support JPEG and PDF file types. Please try again.',
        currentDocuments: documents,
        setCurrentDocuments: setDocuments,
        setDeletedDocs: updateDeletedDocs,
        automationIdPrefix: 'Onboarding_Upload_Documents_File_Upload_Permanent_Residency',
        inMiniProgram: false,
        isPending: isPendingApp,
        hints: getHintsFor(OnboardingDocumentType.PERMANENT_RESIDENCY_TYPE),
      },
      showOnlyIf: () => foreignNationalsEnabled && getPassports().length > 0,
    },
    {
      props: {
        title: 'Proof of banking',
        subtext: 'Accepted file type: JPEG, PDF',
        fileLimitText: 'Maximum size: 3MB',
        documentType: OnboardingDocumentType.BANK_PROOF_TYPE,
        sizeLimit: NON_ID_SIZE_LIMIT,
        allowedFileTypes: ['application/pdf', 'image/jpeg'],
        unsupportedFileTypeErrorMessage:
          'Upload failed, We only support JPEG and PDF file types. Please try again.',
        currentDocuments: documents,
        setCurrentDocuments: setDocuments,
        setDeletedDocs: updateDeletedDocs,
        automationIdPrefix: 'Onboarding_Upload_Documents_File_Upload_ID_Bank_Account_Proof',
        inMiniProgram: inMiniProgram,
        onDocumentUpload: () =>
          TealiumEvents.uploadDocsUpload(
            OnboardingDocumentType.BANK_PROOF_TYPE,
            customerType,
            merchant.isPendingApp,
          ),
        onUploadError: (errorMessage) => TealiumEvents.uploadError(errorMessage),
        customerType: customerType,
        isPending: isPendingApp,
        hints: getHintsFor(OnboardingDocumentType.BANK_PROOF_TYPE),
      },
      showOnlyIf: () => !(businessType === BUSINESS_TYPE_KEY_TO_NAME_LOOKUP.sole_prop && showSov),
    },
    // {
    //   props: {
    //     title: 'Proof of residence',
    //     subtext: 'Accepted file type: JPEG, PDF',
    //     fileLimitText: 'Maximum size: 3MB',
    //     documentType: OnboardingDocumentType.RESIDENCE_TYPE,
    //     sizeLimit: NON_ID_SIZE_LIMIT,
    //     allowedFileTypes: ['application/pdf', 'image/jpeg'],
    //     unsupportedFileTypeErrorMessage:
    //       'Upload failed, We only support JPEG and PDF file types. Please try again.',
    //     currentDocuments: documents,
    //     setCurrentDocuments: setDocuments,
    //     setDeletedDocs: updateDeletedDocs,
    //     automationIdPrefix: 'Onboarding_Upload_Documents_File_Upload_Residence_Proof',
    //     inMiniProgram: inMiniProgram,
    //     onDocumentUpload: () =>
    //       TealiumEvents.uploadDocsUpload(OnboardingDocumentType.RESIDENCE_TYPE, customerType, merchant.isPendingApp),
    //     onUploadError: (errorMessage) => TealiumEvents.uploadError(errorMessage),
    //   },
    //   showOnlyIf: () => sovRequested,
    // customerType: customerType,
    // },
    {
      props: {
        title: 'Resolution letter',
        subtext: 'Accepted file type: JPEG, PDF',
        fileLimitText: 'Maximum size: 3MB',
        documentType: OnboardingDocumentType.COMPANY_RES_TYPE,
        sizeLimit: NON_ID_SIZE_LIMIT,
        allowedFileTypes: ['application/pdf', 'image/jpeg'],
        unsupportedFileTypeErrorMessage:
          'Upload failed, We only support JPEG and PDF file types. Please try again.',
        currentDocuments: documents,
        setCurrentDocuments: setDocuments,
        setDeletedDocs: updateDeletedDocs,
        automationIdPrefix: 'Onboarding_Upload_Documents_File_Upload_Company_Resolution_Letter',
        inMiniProgram: inMiniProgram,
        onDocumentUpload: () =>
          TealiumEvents.uploadDocsUpload(
            OnboardingDocumentType.COMPANY_RES_TYPE,
            customerType,
            merchant.isPendingApp,
          ),
        onUploadError: (errorMessage) => TealiumEvents.uploadError(errorMessage),
        customerType: customerType,
        isPending: isPendingApp,
        hints: getHintsFor(OnboardingDocumentType.COMPANY_RES_TYPE),
      },
      showOnlyIf: () => companyResolutionLetter,
    },
    {
      props: {
        title: 'Resolution letter',
        subtext: 'Accepted file type: JPEG, PDF',
        fileLimitText: 'Maximum size: 3MB',
        documentType: OnboardingDocumentType.TRUST_RES_TYPE,
        sizeLimit: NON_ID_SIZE_LIMIT,
        allowedFileTypes: ['application/pdf', 'image/jpeg'],
        unsupportedFileTypeErrorMessage:
          'Upload failed, We only support JPEG and PDF file types. Please try again.',
        currentDocuments: documents,
        setCurrentDocuments: setDocuments,
        setDeletedDocs: updateDeletedDocs,
        automationIdPrefix: 'Onboarding_Upload_Documents_File_Upload_Trust_Resolution_Letter',
        inMiniProgram: inMiniProgram,
        onDocumentUpload: () =>
          TealiumEvents.uploadDocsUpload(
            OnboardingDocumentType.TRUST_RES_TYPE,
            customerType,
            merchant.isPendingApp,
          ),
        onUploadError: (errorMessage) => TealiumEvents.uploadError(errorMessage),
        customerType: customerType,
        isPending: isPendingApp,
        hints: getHintsFor(OnboardingDocumentType.TRUST_RES_TYPE),
      },
      showOnlyIf: () => trustResolutionLetter,
    },
    {
      props: {
        title: 'NPO founding documentation',
        subtext: 'Accepted file type: JPEG, PDF',
        fileLimitText: 'Maximum size: 3MB',
        documentType: OnboardingDocumentType.NPO_FOUNDING_TYPE,
        sizeLimit: NON_ID_SIZE_LIMIT,
        allowedFileTypes: ['application/pdf', 'image/jpeg'],
        unsupportedFileTypeErrorMessage:
          'Upload failed, We only support JPEG and PDF file types. Please try again.',
        currentDocuments: documents,
        setCurrentDocuments: setDocuments,
        setDeletedDocs: updateDeletedDocs,
        automationIdPrefix: 'Onboarding_Upload_Documents_File_Upload_NPO_Founding_Documentation',
        inMiniProgram: inMiniProgram,
        onDocumentUpload: () =>
          TealiumEvents.uploadDocsUpload(
            OnboardingDocumentType.NPO_FOUNDING_TYPE,
            customerType,
            merchant.isPendingApp,
          ),
        onUploadError: (errorMessage) => TealiumEvents.uploadError(errorMessage),
        customerType: customerType,
        isPending: isPendingApp,
        hints: getHintsFor(OnboardingDocumentType.NPO_FOUNDING_TYPE),
      },
      showOnlyIf: () => npoFoundingDoucmentation,
    },
    {
      props: {
        title: 'Trust deed',
        subtext: 'Accepted file type: JPEG, PDF',
        fileLimitText: 'Maximum size: 3MB',
        documentType: OnboardingDocumentType.TRUST_DEED_TYPE,
        sizeLimit: NON_ID_SIZE_LIMIT,
        allowedFileTypes: ['application/pdf', 'image/jpeg'],
        unsupportedFileTypeErrorMessage:
          'Upload failed, We only support JPEG and PDF file types. Please try again.',
        currentDocuments: documents,
        setCurrentDocuments: setDocuments,
        setDeletedDocs: updateDeletedDocs,
        automationIdPrefix: 'Onboarding_Upload_Documents_File_Upload_Trust_Deed',
        inMiniProgram: inMiniProgram,
        onDocumentUpload: () =>
          TealiumEvents.uploadDocsUpload(
            OnboardingDocumentType.TRUST_DEED_TYPE,
            customerType,
            merchant.isPendingApp,
          ),
        onUploadError: (errorMessage) => TealiumEvents.uploadError(errorMessage),
        customerType: customerType,
        isPending: isPendingApp,
        hints: getHintsFor(OnboardingDocumentType.TRUST_DEED_TYPE),
      },
      showOnlyIf: () => trustDeed,
    },
  ];

  const getFileUploadComponents = () => {
    return config.map((configItem, index) => {
      const props = {
        ...configItem.props,
        titleOriginal: configItem.props.title,
        title: `${index + 1}. ${configItem.props.title}`,
      };

      return (
        (!configItem.showOnlyIf || configItem.showOnlyIf()) && (
          <div
            className={`has-padding-bottom-4 padding-left-right-upload ${
              inMiniProgram ? 'is-mini-app' : ''
            }`}
          >
            <FileUpload key={index} {...props} />
          </div>
        )
      );
    });
  };

  return (
    <div className="UploadDocuments" data-testid="UploadDocuments">
      <div className="has-text-centered">
        <h1 className="heading-four page-title has-padding-top-3">
          {textTable[customerType]?.title}
        </h1>
        <p className="has-padding-bottom-3 has-padding-top-5 page-subtitle has-mobile-padding-top-6 has-padding-x-2">
          {textTable[customerType]?.subTitle}
        </p>
      </div>
      {getFileUploadComponents()}
    </div>
  );
}
